/** API response shape; backend may return PascalCase (Success, Data, Message). */
export interface ApiResponse<T> {
  success?: boolean;
  Success?: boolean;
  message?: string;
  Message?: string;
  data?: T;
  Data?: T;
  errors?: string[];
  Errors?: string[];
}

/** Normalizes API response and extracts data (handles both PascalCase and camelCase from backend). */
export function getResponseData<T>(response: ApiResponse<T> | null | undefined, fallback: T): T {
  if (response == null) return fallback;
  const data = response.data ?? response.Data;
  return data !== undefined && data !== null ? data : fallback;
}

/** Returns true if the API response indicates success. */
export function isResponseSuccess(response: ApiResponse<unknown> | null | undefined): boolean {
  if (response == null) return false;
  return response.success === true || response.Success === true;
}

/** Extracts a single error message from API response (camelCase or PascalCase). */
export function getResponseMessage(response: ApiResponse<unknown> | null | undefined): string {
  if (response == null) return '';
  const msg = response.message ?? response.Message;
  if (msg) return msg;
  const err = response.errors ?? response.Errors;
  return Array.isArray(err) && err.length > 0 ? err[0] : '';
}

/** Reads message from an HttpClient error body (ApiResponse shape or plain text). */
export function getHttpErrorMessage(error: unknown, fallback = ''): string {
  if (error == null || typeof error !== 'object') {
    return fallback;
  }
  const body = (error as { error?: unknown }).error;
  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }
  if (body && typeof body === 'object') {
    const fromApi = getResponseMessage(body as ApiResponse<unknown>);
    if (fromApi) return fromApi;
    const loose = body as { message?: string; Message?: string; title?: string };
    return loose.message ?? loose.Message ?? loose.title ?? fallback;
  }
  return fallback;
}
